import React, { useState } from "react";
import { Link } from "react-router-dom";

// Demo hub (MOVMO-590). Replaces the old root route, which ran a live
// Amadeus flight search that died when Amadeus retired its test
// environment. Lists every product demo in this repo so a sales engineer
// or partner can reach the right flow in one click.
//
// Styling follows the Movmo design system: Inter, the grayWarm neutral
// scale only (never Tailwind gray-*), and the brand gradient for the
// primary CTA. Tokens live in tailwind.config.js.

// The two identity demos live on DIFFERENT registrable domains on purpose:
// the one-click chip is demonstrated carrying a traveler's Movmo session from
// one airline site to another, which only proves anything when the sites are
// not same-site. Each client's redirect_uri is registered on exactly one
// domain. Keep Aero on flyeasyapp and PA on movmoapp.
const AERO_HOST = "mockairline-e2e.app.flyeasyapp.com";
const PA_HOST = "mockairline-e2e.app.movmoapp.com";

interface DemoCardSpec {
    name: string;
    provider: string;
    tagline: string;
    details: string;
    path: string;
    // Registrable domain the demo must be opened on (cross-domain identity
    // story). Omitted = same host as the hub. Ignored on localhost so local
    // dev stays on the dev server.
    host?: string;
    // Flows that read ?mock=true get it on the link so no page ever tries
    // a real provider call.
    mock?: boolean;
    primary?: boolean;
    // Served from public/ (not a router route) — always a plain <a>.
    staticFile?: boolean;
}

// Two products, each demonstrated across BOTH mock-airline domains. Identity
// (the one-click chip) needs two registrable domains to prove the traveler
// carries across airlines; Express Checkout needs them to prove the embedded
// checkout works from any partner origin (cross-site cookies, CHIPS).
interface DemoSection {
    id: string;
    product: string;
    title: string;
    intro: string;
    cards: DemoCardSpec[];
    muted?: boolean;
}

const SECTIONS: DemoSection[] = [
    {
        id: "identity",
        product: "Movmo Identity",
        title: "One-click identity across airlines",
        intro:
            "The traveler clicks the Movmo chip on one airline, links their account and is greeted by name. On the next airline's own domain the same click returns with no screen at all. Open Aero first, then Philippine Airlines. Loyalty linking and the consent notices live in the Philippine Airlines flow.",
        cards: [
            {
                name: "Aero",
                provider: "Identity chip + connect flow",
                tagline: "Connect here first",
                details:
                    "The landing page shows the \"Checkout faster with Movmo\" chip. Click it, allow access on the Movmo consent screen and land back greeted by name; the checkout shows what Movmo shared.",
                path: "/aero",
                host: AERO_HOST,
                mock: true,
                primary: true,
            },
            {
                name: "Aero — script-tag (zero React)",
                provider: "@movmo/connect-button script tag",
                tagline: "One script tag, no bundler",
                details:
                    "The same Aero chip mounted from one <script> tag (MovmoConnect.mountButton()) — no React or bundler on the page. Click it to link, or link on the main Aero demo first and this page greets you from the Aero session.",
                path: "/identity-vanilla.html",
                host: AERO_HOST,
                staticFile: true,
            },
            {
                name: "Philippine Airlines",
                provider: "Identity chip + loyalty linking",
                tagline: "One click on a second domain",
                details:
                    "Same traveler, different registrable domain. Shows the one-click return, the Mabuhay Miles link-or-join step on the consent screen, the denied-scope notice and the loyalty member chip.",
                path: "/philippine-airlines",
                host: PA_HOST,
                mock: true,
            },
        ],
    },
    {
        id: "express-checkout",
        product: "Express Checkout",
        title: "Embedded checkout on partner sites",
        intro:
            "The Movmo Express Checkout button embedded in five different airline sites, alternating between the two mock-airline domains. Each opens the checkout iframe against the Movmo e2e environment; search results are synthesised locally so no provider credentials are needed.",
        cards: [
            {
                name: "Aero",
                provider: "Darwin PSS · mock-aero",
                tagline: "Held inventory, seats and bags",
                details:
                    "Landing → results → checkout. Hold, seat map, bag stepper and payment in the Movmo iframe. The primary sales demo.",
                path: "/aero",
                host: AERO_HOST,
                mock: true,
                primary: true,
            },
            {
                name: "Philippine Airlines",
                provider: "PSS flight-offer payload · mock-pa",
                tagline: "Amadeus-shaped partner payload",
                details:
                    "Search → results → shopping cart → checkout. Shows the button accepting an airline PSS flight-offer payload.",
                path: "/philippine-airlines",
                host: PA_HOST,
                mock: true,
            },
            {
                name: "Clic (KIU)",
                provider: "mock-clic · React",
                tagline: "Screenshot click-through",
                details:
                    "Five-screen replica of the Clic booking flow with real Express Checkout buttons placed on screenshots. Pixel-exact fidelity, fast.",
                path: "/clic",
                host: AERO_HOST,
                mock: true,
            },
            {
                name: "Clic (KIU) — script tag",
                provider: "mock-clic · zero React",
                tagline: "One <script>, one mount()",
                details:
                    "The same Clic click-through with no bundler and no React on the host page. Proof for partners on non-React stacks.",
                path: "/clic-vanilla.html",
                host: PA_HOST,
                staticFile: true,
            },
            {
                name: "Scandinavian Airlines",
                provider: "PSS flight-offer payload · mock-pa",
                tagline: "Multi-cabin fare comparison",
                details:
                    "Search → results → travelers. Economy, premium and business priced side by side, then the button hands off to Express Checkout.",
                path: "/scandinavian-airlines",
                host: PA_HOST,
            },
            {
                name: "Dohop",
                provider: "Dohop NDC",
                tagline: "Virtual interlining",
                details:
                    "Search → results → checkout through the Dohop Connect proxy. NDC-style create-offer → price → order.",
                path: "/dohop",
                host: AERO_HOST,
            },
        ],
    },
    {
        id: "tools",
        product: "Engineering",
        title: "Engineering tools",
        intro:
            "Test surfaces for the packages and sandboxes the demos depend on. Not part of a customer walkthrough.",
        muted: true,
        cards: [
            {
                name: "Connect button",
                provider: "@movmo/connect-button",
                tagline: "Identity connect test surface",
                details:
                    "Starts the OAuth connect flow against auth.e2e.movmo.io and lands on the local callback route. Used to verify the package before publishing.",
                path: "/connect-button",
            },
            {
                name: "Spreedly tokenizer",
                provider: "Spreedly Hosted Fields",
                tagline: "Capture a sandbox payment token",
                details:
                    "Tokenizes a test card in the E2E Spreedly sandbox so automated tests can drive a vaulted booking end to end.",
                path: "/spreedly-tokenizer",
            },
        ],
    },
];

function isLocalhost(): boolean {
    if (typeof window === "undefined") return false;
    const h = window.location.hostname;
    return h === "localhost" || h === "127.0.0.1";
}

function buildHref(spec: DemoCardSpec, internal: boolean): string {
    const params = new URLSearchParams();
    if (spec.mock) params.set("mock", "true");
    if (internal) params.set("mv_internal", "1");
    const qs = params.toString();
    const rel = qs ? `${spec.path}?${qs}` : spec.path;
    if (spec.host && !isLocalhost()) {
        return `https://${spec.host}${rel}`;
    }
    return rel;
}

// True when the link leaves the current origin — rendered as a plain <a>
// so the browser does a full navigation to the other domain.
function isExternal(href: string, spec: DemoCardSpec): boolean {
    return href.startsWith("https://") || Boolean(spec.staticFile);
}

const DemoCard: React.FC<{
    spec: DemoCardSpec;
    sectionId: string;
    internal: boolean;
    muted?: boolean;
}> = ({ spec, sectionId, internal, muted }) => {
    const href = buildHref(spec, internal);
    return (
        <div
            data-testid={`demo-card-${sectionId}-${spec.path.replace(/[\/.]/g, "")}`}
            className={`flex flex-col rounded-xl border border-grayWarm-200 p-6 shadow-sm ${
                muted ? "bg-grayWarm-100" : "bg-white"
            }`}
        >
            <div className="mb-1 flex items-start justify-between gap-3">
                <h3 className="text-lg font-semibold text-grayWarm-950">
                    {spec.name}
                </h3>
                {spec.primary && (
                    <span className="rounded-full bg-brand-50 px-2.5 py-0.5 text-xs font-medium text-brand-600">
                        Start here
                    </span>
                )}
            </div>
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-grayWarm-500">
                {spec.provider}
            </p>
            {spec.host ? (
                <p className="mb-3 text-xs text-grayWarm-500">
                    Opens on{" "}
                    <span className="font-medium text-grayWarm-700">
                        {spec.host}
                    </span>
                </p>
            ) : (
                <div className="mb-2" />
            )}
            <p className="mb-1 text-sm font-medium text-grayWarm-700">
                {spec.tagline}
            </p>
            <p className="mb-5 flex-1 text-sm text-grayWarm-600">
                {spec.details}
            </p>
            {muted ? (
                <Link
                    to={href}
                    className="text-sm font-semibold text-grayWarm-700 underline-offset-4 hover:text-grayWarm-950 hover:underline"
                >
                    Open tool →
                </Link>
            ) : isExternal(href, spec) ? (
                <a
                    href={href}
                    className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-brand-500 to-brand-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:opacity-95"
                >
                    Open demo
                </a>
            ) : (
                <Link
                    to={href}
                    className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-brand-500 to-brand-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:opacity-95"
                >
                    Open demo
                </Link>
            )}
        </div>
    );
};

const DemoHub: React.FC = () => {
    // Team-run demos should be excluded from partner analytics (MOVMO-478),
    // so the flag defaults ON. Unticking it is remembered for the session.
    const [internal, setInternal] = useState<boolean>(() => {
        try {
            const v = window.sessionStorage.getItem("movmo_demo_hub_internal");
            return v === null ? true : v === "1";
        } catch {
            return true;
        }
    });
    const toggleInternal = (checked: boolean) => {
        setInternal(checked);
        try {
            window.sessionStorage.setItem(
                "movmo_demo_hub_internal",
                checked ? "1" : "0",
            );
        } catch {
            /* ignore */
        }
    };

    const host =
        typeof window !== "undefined" ? window.location.host : "";

    return (
        <div className="min-h-screen bg-grayWarm-50 text-grayWarm-950">
            <header className="border-b border-grayWarm-200 bg-white">
                <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
                    <div>
                        <div className="text-2xl font-bold tracking-tight text-grayWarm-950">
                            Movmo
                        </div>
                        <div className="text-sm text-grayWarm-600">
                            Product demos — Identity and Express Checkout
                        </div>
                    </div>
                    <label className="flex cursor-pointer items-center gap-2 text-sm text-grayWarm-700">
                        <input
                            type="checkbox"
                            checked={internal}
                            onChange={(e) => toggleInternal(e.target.checked)}
                            className="h-4 w-4 rounded border-grayWarm-300 text-brand-500 focus:ring-brand-500"
                        />
                        Flag session as internal QA
                    </label>
                </div>
            </header>

            <main className="mx-auto max-w-6xl px-6 py-10">
                {SECTIONS.map((section, i) => (
                    <section
                        key={section.id}
                        id={section.id}
                        className={i === SECTIONS.length - 1 ? "" : "mb-14"}
                    >
                        <div className="mb-1 flex items-center gap-3">
                            <span
                                className={`rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide ${
                                    section.muted
                                        ? "bg-grayWarm-200 text-grayWarm-700"
                                        : "bg-brand-50 text-brand-600"
                                }`}
                            >
                                {section.product}
                            </span>
                            <h2 className="text-2xl font-semibold text-grayWarm-950">
                                {section.title}
                            </h2>
                        </div>
                        <p className="mb-6 max-w-3xl text-sm text-grayWarm-600">
                            {section.intro}
                        </p>
                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                            {section.cards.map((spec) => (
                                <DemoCard
                                    key={`${section.id}-${spec.path}`}
                                    spec={spec}
                                    sectionId={section.id}
                                    internal={internal}
                                    muted={section.muted}
                                />
                            ))}
                        </div>
                    </section>
                ))}
            </main>

            <footer className="border-t border-grayWarm-200 py-6">
                <div className="mx-auto max-w-6xl px-6 text-xs text-grayWarm-500">
                    {host ? `Serving from ${host}. ` : ""}
                    Append <code className="rounded bg-grayWarm-100 px-1">?mv_internal=1</code>{" "}
                    to any demo link to exclude the session from partner
                    analytics.
                </div>
            </footer>
        </div>
    );
};

export default DemoHub;
